import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { formatLockoutCountdown } from "@/lib/cognitoAuthErrors";
import { SIGNUP_ENABLED } from "@/lib/loginConfig";
import { ENTRA_SSPR_URL, ENTRA_NO_ACCESS_MESSAGE, ENTRA_NO_ACCESS_QUERY, ENTRA_NO_ACCESS_VALUE } from "@/lib/entraAuth";
import {
  clearStoredLoginLockout,
  readStoredLoginLockout,
  storeLoginLockout,
} from "@/lib/loginLockout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AlertCircle, KeyRound, Loader2, Lock, Mail } from "lucide-react";

const POOL_MIN_PASSWORD = 12;

export default function Login() {
  const {
    login,
    forgotPassword,
    confirmForgotPassword,
    completeNewPassword,
    cancelNewPasswordChallenge,
    beginEntraLogin,
    isCognitoConfigured,
    isLocalDevBypassEnabled,
    idpMode,
    idpUnavailable,
  } = useAuth();
  const localDevBypass = isLocalDevBypassEnabled();
  const entraMode = idpMode === "entra";
  const canUseCognitoFlows = isCognitoConfigured && !localDevBypass && !entraMode;

  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [challengeNewPassword, setChallengeNewPassword] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [lockoutUntil, setLockoutUntil] = useState(null);
  const [accountLocked, setAccountLocked] = useState(false);
  const [, setLockoutNow] = useState(() => Date.now());

  const isSignInLocked =
    mode === "signin" &&
    !challengeNewPassword &&
    lockoutUntil != null &&
    lockoutUntil > Date.now();

  // Lockout blocks sign-in only — forgot password stays available while locked (Phase 2).
  const isSignInBlocked =
    mode === "signin" && !challengeNewPassword && (isSignInLocked || accountLocked);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get(ENTRA_NO_ACCESS_QUERY) === ENTRA_NO_ACCESS_VALUE) {
        setError(ENTRA_NO_ACCESS_MESSAGE);
        params.delete(ENTRA_NO_ACCESS_QUERY);
        const query = params.toString();
        const next = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash || ""}`;
        window.history.replaceState({}, "", next);
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (mode !== "signin" || challengeNewPassword) {
      return;
    }
    const stored = readStoredLoginLockout(email);
    if (stored) {
      setLockoutUntil(stored);
    }
  }, [email, mode, challengeNewPassword]);

  useEffect(() => {
    if (!lockoutUntil || lockoutUntil <= Date.now()) {
      return undefined;
    }
    const id = window.setInterval(() => {
      setLockoutNow(Date.now());
      if (Date.now() >= lockoutUntil) {
        setLockoutUntil(null);
        clearStoredLoginLockout(email);
        setError("");
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [lockoutUntil, email]);

  const resetChallenge = () => {
    cancelNewPasswordChallenge();
    setChallengeNewPassword(false);
    setNewPassword("");
    setConfirmNewPassword("");
    setResetCode("");
    setError("");
    setInfo("");
  };

  const clearLockoutState = () => {
    clearStoredLoginLockout(email);
    setLockoutUntil(null);
    setAccountLocked(false);
  };

  const goToSignIn = (message) => {
    resetChallenge();
    clearLockoutState();
    setMode("signin");
    setPassword("");
    setResetCode("");
    setNewPassword("");
    setConfirmNewPassword("");
    setError("");
    if (message) setInfo(message);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);

    try {
      if (challengeNewPassword) {
        if (newPassword !== confirmNewPassword) {
          setError("New passwords do not match.");
          return;
        }
        if (newPassword.length < POOL_MIN_PASSWORD) {
          setError(`Use at least ${POOL_MIN_PASSWORD} characters (pool policy).`);
          return;
        }
        const result = await completeNewPassword(newPassword);
        if (!result.success) {
          setError(result.error || "Could not set password");
        }
        return;
      }

      if (mode === "forgot-request") {
        const result = await forgotPassword(email);
        if (!result.success) {
          setError(result.error || "Could not send reset code");
          return;
        }
        setInfo(
          "If an account exists for this email, a verification code was sent. Check your inbox.",
        );
        setMode("forgot-confirm");
        return;
      }

      if (mode === "forgot-confirm") {
        if (newPassword !== confirmNewPassword) {
          setError("New passwords do not match.");
          return;
        }
        if (newPassword.length < POOL_MIN_PASSWORD) {
          setError(`Use at least ${POOL_MIN_PASSWORD} characters (pool policy).`);
          return;
        }
        const result = await confirmForgotPassword(email, resetCode, newPassword);
        if (!result.success) {
          setError(result.error || "Could not reset password");
          return;
        }
        goToSignIn("Password updated. Sign in with your new password.");
        return;
      }

      if (isSignInBlocked) {
        return;
      }

      // Self-service sign-up: see SIGNUP_ENABLED in @/lib/loginConfig.js and signUp in AuthContext.js
      const result = await login(email, password);
      if (result.newPasswordRequired) {
        setChallengeNewPassword(true);
        setInfo(
          "Your account still uses a temporary password. Choose a permanent one below.",
        );
      } else if (!result.success) {
        if (result.accountLocked) {
          setAccountLocked(true);
          setError("");
        } else {
          setAccountLocked(false);
          setError(result.error || "Sign in failed");
        }
        if (result.lockoutUntil) {
          setLockoutUntil(result.lockoutUntil);
          storeLoginLockout(email, result.lockoutUntil);
        }
      } else {
        clearLockoutState();
      }
    } finally {
      setLoading(false);
    }
  };

  const title = challengeNewPassword
    ? "Set a new password"
    : entraMode
      ? "Welcome back"
    : mode === "forgot-request"
      ? "Forgot password"
      : mode === "forgot-confirm"
        ? "Reset password"
        : "Welcome back";

  const description = challengeNewPassword
    ? "Required after an admin-created account or temporary password."
    : entraMode
      ? "Sign in with your Microsoft work account"
    : mode === "forgot-request"
      ? "Enter your email and we will send a verification code."
      : mode === "forgot-confirm"
        ? "Enter the code from your email and choose a new password."
        : "Sign in with your email and password";

  const submitLabel = challengeNewPassword
    ? "Save password and continue"
    : mode === "forgot-request"
      ? "Send reset code"
      : mode === "forgot-confirm"
        ? "Reset password"
        : "Sign in";

  const loadingLabel = challengeNewPassword
    ? "Saving..."
    : mode === "forgot-request"
      ? "Sending..."
      : mode === "forgot-confirm"
        ? "Resetting..."
        : "Signing in...";

  const showEmail = !challengeNewPassword;
  const showPassword = mode === "signin";
  const showResetCode = mode === "forgot-confirm";
  const showNewPasswordFields =
    challengeNewPassword || mode === "forgot-confirm";

  const showCognitoLockoutNotice =
    isSignInLocked && mode === "signin" && !challengeNewPassword;
  const showAppAccountLockedNotice =
    accountLocked && mode === "signin" && !challengeNewPassword && !showCognitoLockoutNotice;

  return (
    <div className="min-h-screen bg-cp-page flex items-center justify-center p-4 relative overflow-hidden">
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 bg-cp-grad shadow-cp-accent">
            <Lock className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-cp-fg mb-2">Halo</h1>
          <p className="text-cp-muted">SAP AI Customization Engine</p>
          <p className="mt-2 text-[10.5px] font-semibold uppercase tracking-[0.18em] text-cp-faint">
            Authenticated access
          </p>
        </div>

        <Card className="border-cp-line shadow-cp backdrop-blur-sm">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl text-cp-fg text-center">{title}</CardTitle>
            <CardDescription className="text-cp-muted text-center">
              {description}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!entraMode && !idpUnavailable && !isCognitoConfigured && localDevBypass && (
              <div className="mb-4 p-3 bg-cp-indigo/10 border border-cp-indigo/30 rounded-lg text-cp-cyan text-sm">
                Local demo mode: Cognito is not configured. Sign in with any
                email and password to continue against{" "}
                <code className="text-xs bg-cp-inset px-1 rounded">
                  {process.env.REACT_APP_BACKEND_URL || "localhost gateway"}
                </code>
                .
              </div>
            )}
            {!entraMode && !idpUnavailable && !isCognitoConfigured && !localDevBypass && (
              <div className="mb-4 flex items-start gap-2 p-3 bg-cp-faint/10 border border-[color-mix(in_srgb,var(--mod)_30%,transparent)] rounded-lg text-cp-mod text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  Cognito is not configured. Set{" "}
                  <code className="text-xs bg-cp-inset px-1 rounded">
                    REACT_APP_COGNITO_USER_POOL_ID
                  </code>{" "}
                  and{" "}
                  <code className="text-xs bg-cp-inset px-1 rounded">
                    REACT_APP_COGNITO_CLIENT_ID
                  </code>{" "}
                  at build time, or point{" "}
                  <code className="text-xs bg-cp-inset px-1 rounded">
                    REACT_APP_BACKEND_URL
                  </code>{" "}
                  at localhost for local demo bypass.
                </span>
              </div>
            )}

            {idpUnavailable && (
              <div className="mb-4 flex items-start gap-2 p-3 bg-[color-mix(in_srgb,var(--crit)_12%,transparent)] border border-[color-mix(in_srgb,var(--crit)_25%,transparent)] rounded-lg text-cp-crit text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  Authentication is unavailable. Halo could not read the identity
                  provider setting. Try again, or contact an administrator.
                </span>
              </div>
            )}

            {entraMode && !idpUnavailable && (
              <div className="space-y-4">
                {error && (
                  <div
                    className="flex items-start gap-2 p-3 bg-[color-mix(in_srgb,var(--crit)_12%,transparent)] border border-[color-mix(in_srgb,var(--crit)_25%,transparent)] rounded-lg text-cp-crit text-sm"
                    data-testid="login-entra-error"
                  >
                    <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}
                <Button
                  type="button"
                  className="w-full font-medium py-2.5"
                  onClick={beginEntraLogin}
                  data-testid="login-entra-submit"
                >
                  Sign in
                </Button>
                <button
                  type="button"
                  className="w-full text-sm text-cp-cyan hover:text-cp-cyan transition-colors"
                  onClick={() => {
                    window.open(ENTRA_SSPR_URL, "_blank", "noopener,noreferrer");
                  }}
                  data-testid="login-forgot-password"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {!entraMode && !idpUnavailable && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-[color-mix(in_srgb,var(--crit)_12%,transparent)] border border-[color-mix(in_srgb,var(--crit)_25%,transparent)] rounded-lg text-cp-crit text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              {info && (
                <div className="p-3 bg-cp-indigo/10 border border-cp-indigo/20 rounded-lg text-cp-cyan text-sm">
                  {info}
                </div>
              )}
              {showCognitoLockoutNotice && (
                <div
                  className="flex items-center gap-2 p-3 bg-cp-faint/10 border border-[color-mix(in_srgb,var(--mod)_30%,transparent)] rounded-lg text-cp-mod text-sm"
                  data-testid="login-lockout-notice"
                >
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>
                    Too many failed attempts. Try again in{" "}
                    <strong>{formatLockoutCountdown(lockoutUntil)}</strong>, or
                    use Forgot password.
                  </span>
                </div>
              )}
              {showAppAccountLockedNotice && (
                <div
                  className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-200 text-sm"
                  data-testid="login-account-locked-notice"
                >
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>
                    Too many failed sign-in attempts. Use Forgot password or contact an
                    administrator.
                  </span>
                </div>
              )}

              {showEmail && (
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-cp-muted">
                    Email
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-cp-faint" />
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-cp-inset border-cp-line text-cp-fg placeholder:text-cp-faint focus:border-cp-indigo focus:ring-cp-indigo/20"
                      required={!localDevBypass}
                      readOnly={mode === "forgot-confirm"}
                      data-testid="login-email"
                    />
                  </div>
                </div>
              )}

              {challengeNewPassword && (
                <div className="space-y-2">
                  <Label className="text-cp-muted">Account</Label>
                  <p className="text-sm text-cp-faint pl-1">{email}</p>
                </div>
              )}

              {showResetCode && (
                <div className="space-y-2">
                  <Label htmlFor="reset-code" className="text-cp-muted">
                    Verification code
                  </Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-cp-faint" />
                    <Input
                      id="reset-code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="123456"
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value)}
                      className="pl-10 bg-cp-inset border-cp-line text-cp-fg placeholder:text-cp-faint focus:border-cp-indigo focus:ring-cp-indigo/20"
                      required
                      data-testid="login-reset-code"
                    />
                  </div>
                </div>
              )}

              {showPassword && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-cp-muted">
                      Password
                    </Label>
                    {mode === "signin" && canUseCognitoFlows && (
                      <button
                        type="button"
                        className="text-xs text-cp-cyan hover:text-cp-cyan transition-colors"
                        onClick={() => {
                          setMode("forgot-request");
                          setError("");
                          setInfo("");
                          setPassword("");
                        }}
                        data-testid="login-forgot-password"
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-cp-faint" />
                    <Input
                      id="password"
                      type="password"
                      autoComplete={
                        mode === "signin" ? "current-password" : "new-password"
                      }
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 bg-cp-inset border-cp-line text-cp-fg placeholder:text-cp-faint focus:border-cp-indigo focus:ring-cp-indigo/20"
                      required={!localDevBypass}
                      minLength={1}
                      data-testid="login-password"
                    />
                  </div>
                </div>
              )}

              {showNewPasswordFields && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="new-password" className="text-cp-muted">
                      {mode === "forgot-confirm" ? "New password" : "New password"}
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-cp-faint" />
                      <Input
                        id="new-password"
                        type="password"
                        autoComplete="new-password"
                        placeholder="••••••••"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="pl-10 bg-cp-inset border-cp-line text-cp-fg placeholder:text-cp-faint focus:border-cp-indigo focus:ring-cp-indigo/20"
                        required={!localDevBypass}
                        minLength={POOL_MIN_PASSWORD}
                        data-testid="login-new-password"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-new-password" className="text-cp-muted">
                      Confirm new password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-cp-faint" />
                      <Input
                        id="confirm-new-password"
                        type="password"
                        autoComplete="new-password"
                        placeholder="••••••••"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        className="pl-10 bg-cp-inset border-cp-line text-cp-fg placeholder:text-cp-faint focus:border-cp-indigo focus:ring-cp-indigo/20"
                        required={!localDevBypass}
                        minLength={POOL_MIN_PASSWORD}
                        data-testid="login-confirm-new-password"
                      />
                    </div>
                    <p className="text-xs text-cp-faint">
                      At least {POOL_MIN_PASSWORD} characters with upper,
                      lower, number, and symbol (Cognito pool policy).
                    </p>
                  </div>
                </>
              )}

              <Button
                type="submit"
                className="w-full font-medium py-2.5"
                disabled={loading || isSignInBlocked}
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {loadingLabel}
                  </>
                ) : (
                  submitLabel
                )}
              </Button>

              {challengeNewPassword ? (
                <button
                  type="button"
                  className="w-full text-sm text-cp-faint hover:text-white transition-colors"
                  onClick={resetChallenge}
                >
                  Back to sign in
                </button>
              ) : mode === "forgot-request" || mode === "forgot-confirm" ? (
                <button
                  type="button"
                  className="w-full text-sm text-cp-faint hover:text-white transition-colors"
                  onClick={() => goToSignIn()}
                  data-testid="login-back-to-signin"
                >
                  Back to sign in
                </button>
              ) : SIGNUP_ENABLED ? (
                <button
                  type="button"
                  className="w-full text-sm text-cp-faint hover:text-white transition-colors"
                  onClick={() => {
                    setMode(mode === "signin" ? "signup" : "signin");
                    setError("");
                    setInfo("");
                  }}
                  disabled={localDevBypass}
                >
                  {mode === "signin"
                    ? "Need an account? Sign up"
                    : "Already have an account? Sign in"}
                </button>
              ) : null}
            </form>
            )}

            <div className="mt-6 pt-4 border-t border-cp-line">
              <p className="text-center text-xs text-cp-faint">
                SAP AI Customization Engine
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 flex items-center justify-center gap-2">
          <span className="text-sm text-cp-muted">Powered by</span>
          <img
            src="https://customer-assets.emergentagent.com/job_ai-customizer-1/artifacts/ziz1wre7_rtslogo-with-name-transparent-300-ppi-RGB.png"
            alt="Resolve Tech Solutions"
            className="h-6 object-contain dark:brightness-0 dark:invert opacity-70"
          />
        </div>
      </div>
    </div>
  );
}
